import { useState, type SyntheticEvent } from 'react';
import { fetchQuote, fmtBps, toRawAmount, toolErrorText, type QuotePayload } from '../api/tools';
import { MAINNET } from '../lib/chain';
import { QUOTE_DECIMALS, QUOTE_POOLS, STOCK_DECIMALS, type QuotePool } from '../lib/quotePools';

type State =
  | { s: 'idle' }
  | { s: 'invalid' }
  | { s: 'loading' }
  | { s: 'done'; q: QuotePayload; pool: QuotePool; zeroForOne: boolean; amount: string }
  | { s: 'failed'; text: string };

function Result({ st }: { st: State }) {
  if (st.s === 'invalid') return <p>Enter a positive amount (up to 6 decimals for USDG, 18 for a Stock Token).</p>;
  if (st.s === 'loading') return <p>Asking the deployed hook…</p>;
  if (st.s === 'failed') return <><h4>No quote</h4><p>{st.text}</p></>;
  if (st.s !== 'done') return null;
  const { q, pool, zeroForOne, amount } = st;
  const spend = zeroForOne ? pool.quote : pool.stock;
  const get = zeroForOne ? pool.stock : pool.quote;
  return (
    <>
      <h4>{fmtBps(q.feeBps)}</h4>
      <dl>
        <dt>Swap</dt><dd>{amount} {spend} for {get}</dd>
        <dt>Fee</dt><dd className="hot">{fmtBps(q.feeBps)} ({q.feePips} pips)</dd>
        <dt>Feed</dt><dd>{q.stale ? 'stale: the fee follows the measured gap' : 'fresh: the pool tier fee applies'}</dd>
        <dt>Gap</dt><dd>{q.gapBps === null ? 'not measured' : fmtBps(q.gapBps)}</dd>
        {q.source && <><dt>Source</dt><dd>{q.source}</dd></>}
      </dl>
      <p>Quote from the deployed contract: the hook's own fee function on {pool.chainId === MAINNET ? 'Robinhood Chain mainnet' : 'Robinhood Chain testnet'}, read by the worker API for this amount and direction. Measured at the time of the call; the fee returns to the tier fee once the feed prints.</p>
    </>
  );
}

/** "Fee quote": GET /api/data/quote?chainId=&pool=&amountIn=&zeroForOne= for a BellHook pool. */
export function FeeQuote() {
  const [poolKey, setPoolKey] = useState(QUOTE_POOLS[1]?.key ?? 'testnet');
  const [amount, setAmount] = useState('100');
  const [zeroForOne, setZeroForOne] = useState(true);
  const [st, setSt] = useState<State>({ s: 'idle' });
  const pool = QUOTE_POOLS.find((p) => p.key === poolKey) ?? QUOTE_POOLS[0];

  const onSubmit = (ev: SyntheticEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (!pool) return;
    const raw = toRawAmount(amount, zeroForOne ? QUOTE_DECIMALS : STOCK_DECIMALS);
    if (raw === null) { setSt({ s: 'invalid' }); return; }
    setSt({ s: 'loading' });
    const at = { pool, zeroForOne, amount: amount.trim() };
    fetchQuote({ chainId: pool.chainId, pool: pool.pool, amountIn: raw, zeroForOne })
      .then((q) => { setSt({ s: 'done', q, ...at }); })
      .catch((e: unknown) => { setSt({ s: 'failed', text: toolErrorText(e) }); });
  };
  const cls = st.s === 'done' ? 'result ok' : st.s === 'failed' || st.s === 'invalid' ? 'result unknown' : 'result';

  return (
    <section className="section" id="quote">
      <h2>Fee quote</h2>
      <p className="table-intro">Inspect the fee before committing USDG. Pick a BellHook pool, an amount and a direction; the worker API asks the deployed hook what it would charge for that swap right now, and whether the feed counts as stale.</p>
      <form className="checker quote-form" id="quote-form" onSubmit={onSubmit}>
        <label htmlFor="quote-pool" className="sr-only">Pool</label>
        <select id="quote-pool" value={poolKey} onChange={(e) => { setPoolKey(e.target.value); }}>
          {QUOTE_POOLS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <label htmlFor="quote-dir" className="sr-only">Direction</label>
        <select id="quote-dir" value={zeroForOne ? 'buy' : 'sell'} onChange={(e) => { setZeroForOne(e.target.value === 'buy'); }}>
          <option value="buy">{pool ? `${pool.quote} in, ${pool.stock} out` : 'buy'}</option>
          <option value="sell">{pool ? `${pool.stock} in, ${pool.quote} out` : 'sell'}</option>
        </select>
        <label htmlFor="quote-amount" className="sr-only">Amount in</label>
        <input id="quote-amount" type="text" inputMode="decimal" autoComplete="off" value={amount} onChange={(e) => { setAmount(e.target.value); }} />
        <button type="submit" className="button" disabled={st.s === 'loading'}>Quote</button>
      </form>
      <div className={cls} id="quote-result" aria-live="polite" hidden={st.s === 'idle'}><Result st={st} /></div>
      <p className="fineprint">The mainnet pool has no liquidity yet, so a mainnet quote is the fee rule applied to the current feed and pool state, not a fill. The fee parameters live in BellConfig behind a 24 hour timelock.</p>
    </section>
  );
}
